import React, { useEffect, useState } from "react";
// import ProductDetails from "../ProductDetails";
import Rating from "@mui/material/Rating";
import { FaGoogle, FaRegHeart, FaAmazon, FaSpinner, FaHeart } from "react-icons/fa";
import { BsBox, BsInfoCircle } from "react-icons/bs";
import {
  cmToInches,
  formatLength,
  formatNumber,
  formatWeight,
} from "../../utils/helper";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useSelector } from "react-redux";
import { FaExclamation, FaRegCopy } from "react-icons/fa6";
import { GoDotFill } from "react-icons/go";
import {
  addFavorite,
  checkAmazonAuthStatus,
  checkAmazonEligibility,
  removeFavorite,
} from "../../services/product";
import TooltipSimple from "../Common/TooltipSimple";
import { toast } from "sonner";
import { getAlertData } from "../../utils/helper";

const Overview = ({
  asin,
  eanList,
  data,
  basicInfo,
  amazonFees,
  salesData,
  loading,
  rating,
  settingsData,
  amazonLink,
  dimensions,
  description,
  referralFeePercent,
  alerts,
  scores,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [scoreValue, setScoreValue] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [visibleAlerts, setVisibleAlerts] = useState([]);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  const getFinalScore = (scores) => {
    // calculate the final score
    const weights = {
      tier1: 0.4, // bbPriceScore, offerScore, salesScore
      tier2: 0.25, // bbPercentageScore, bbSuppressionScore
      tier3: 0.2, // brandScore, amazonStockScore
      tier4: 0.1, // reviewRatingScore, listingAgeScore
    };
    const tier1Score =
      (scores.bbPriceScore?.score +
        scores.offerScore?.score +
        scores.salesScore?.score) /
      3;
    const tier2Score =
      (scores.bbPercentageScore?.score + scores.bbSuppressionScore?.score) / 2;
    const tier3Score =
      (scores.brandScore?.score + scores.amazonStockScore?.score) / 2;
    const tier4Score =
      (scores.reviewRatingScore?.score + scores.listingAgeScore?.score) / 2;

    const weightedScore =
      tier1Score * weights.tier1 +
      tier2Score * weights.tier2 +
      tier3Score * weights.tier3 +
      tier4Score * weights.tier4;

    return ((weightedScore / 20) * 10).toFixed(1);
  };

  useEffect(() => {
    if (scores) {
      const updatedReasons = [];

      if (basicInfo?.brand === "Generic") {
        updatedReasons.push({
          reason:
            "This is a generic branded listing which is not good to sell. It can actually affect your account health. We recommend avoiding these products",
          alert: true,
        });
      }

      const bsrPercentage =
        parseFloat(data?.BSR?.percentage?.replace("%", "")) || 0;
      const hasHighBSR = bsrPercentage > 1.5;
      const hasNoSales = salesData?.sales_estimation?.monthly_sales <= 0;

      if (hasHighBSR) {
        updatedReasons.push({
          reason: "This product has a sales rank percentage greater than 1.5%",
          alert: true,
        });
      }

      Object.entries(scores).forEach(([key, s]) => {
        if (s && s.reason && key !== "saleScore") {
          updatedReasons.push({ reason: s.reason, alert: s.alert });
        }
      });
      setReasons(updatedReasons);
      let finalScore = getFinalScore(scores);
      setScoreValue(finalScore);
    }
  }, [scores, data]);

  useEffect(() => {
    const { alertData } = getAlertData(alerts, basicInfo);
    const visibleAlertsOnly = alertData.filter((alert) => !alert.hidden);
    setVisibleAlerts(visibleAlertsOnly);
  }, [alerts, basicInfo]);

  const checkEligibility = async () => {
    if (!asin) return;
    setLoadingEligibility(true);
    try {
      const response = await checkAmazonEligibility(asin);

      if (response) {
        setEligibilityData(response);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setLoadingEligibility(false);
    }
  };

  const getEligibilityStatus = (eligible) => {
    if (eligible === null || eligible === undefined) return null;
    return eligible;
  };

  const getRestrictionsTooltipJSX = (restrictions) => {
    if (!restrictions || restrictions.length === 0) return null;

    return (
      <div className="flex flex-col gap-y-1 text-left justify-start">
        <div className="text-white font-semibold">Reasons:</div>
        {restrictions.map((restriction, i) =>
          restriction.reasons.map((reason, j) => (
            <div key={`${i}-${j}`} className="text-yellow-500 flex gap-x-1">
              <FaExclamation className="w-2 h-3 mt-0.5" />
              <p className="w-[95%]">{reason.message}</p>
            </div>
          ))
        )}
      </div>
    );
  };

  const checkAuthStatus = async () => {
    setCheckingAuth(true);
    const response = await checkAmazonAuthStatus();
    if (response) {
      setAuthStatus(response);
    }
    setCheckingAuth(false);
  };

  const asinValue = asin ? asin : "-";
  const eanValue = eanList?.length > 0 && eanList[0] ? eanList[0] : "";

  const handleAmazonEligibility = () => {
    if (authStatus?.isConnected) {
      checkEligibility();
    } else {
      window.open(`https://app.scanprofit.com/dashboard`, "_blank");
    }
  };

  const handleAddFavorite = async () => {
    const response = await addFavorite(asin);
    if (response) {
      setIsFavorite(true);
      toast.success("Product added to favorites");
    }
  };

  const handleRemoveFavorite = async () => {
    const response = await removeFavorite(asin);
    if (response) {
      setIsFavorite(false);
      toast.success("Product removed from favorites");
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, [asin]);

  return (
    <React.Fragment>
      <div className="flex md:flex-row flex-col w-full md:p-4 p-0 md:gap-0 gap-4 rounded-2xl">
        <div
          className={`md:w-[40%] w-full flex items-center rounded-lg overflow-hidden ${
            data?.image && !loading ? "p-6 bg-white" : "py-0"
          }`}
        >
          {!data?.image || loading ? (
            <div
              className="w-full flex items-center justify-center h-full bg-gray-300 rounded"
              style={{
                border: "0.5px solid #376C46",
                background: "rgba(118, 249, 155, 0.15)",
                borderRadius: "20px",
              }}
            >
              <svg
                className="w-10 h-10 text-gray-200 dark:text-[#76F99B]"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="#1c211d"
                viewBox="0 0 20 18"
              >
                <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
              </svg>
            </div>
          ) : (
            <img
              src={data?.image}
              alt="Product 1"
              className="w-full h-36 object-contain"
              style={{
                borderRadius: "20px",
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-y-4 py-y md:px-6 md:w-[60%] w-full">
          <div className="flex">
            <div className="text-primary text-left w-full">
              {!loading ? (
                <>
                  <div className="text-primary font-bold text-xs">
                    {basicInfo?.brand ? basicInfo?.brand : ""}
                  </div>
                  <div className="text-white group relative md:text-2xl text-lg font-bold text-whitecursor-default">
                    {basicInfo?.productName
                      ? basicInfo.productName.length > 60
                        ? basicInfo.productName.substring(0, 60) + "..."
                        : basicInfo.productName
                      : "--"}
                    <TooltipSimple
                      text={basicInfo?.productName}
                      width={"extra"}
                    />
                  </div>
                  <div
                    className="text-white"
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    {basicInfo?.category ? basicInfo?.category : ""}
                  </div>
                  <div className="flex items-end mt-1">
                    <Rating
                      name="read-only"
                      value={Number(rating?.rating)}
                      precision={0.1}
                      readOnly
                      style={{
                        fontSize: "18px",
                        color: "#75fa9b",
                      }}
                    />
                    <span className="text-primary text-xs font-semibold ml-1">
                      {rating?.ratings_total
                        ? formatNumber(Number(rating.ratings_total))
                        : 0}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={isFavorite ? handleRemoveFavorite : handleAddFavorite} className="bg-secondary hover:bg-primary hover:text-secondary transition duration-300 text-primary p-1.5 px-3 rounded-md flex items-center justify-center">
                      {isFavorite ? <FaHeart className="w-4 h-4" /> : <FaRegHeart className="w-4 h-4" />}
                    </button>

                    <div className="group relative">
                      <button
                        onClick={() => {
                          if (loadingEligibility) return;
                          if (eligibilityData && authStatus?.isConnected)
                            return;
                          handleAmazonEligibility();
                        }}
                        className={`transition duration-300 ${
                          !authStatus?.isConnected
                            ? "text-primary bg-secondary hover:bg-primary hover:text-secondary"
                            : loadingEligibility || !eligibilityData
                            ? "border border-yellow-700 text-yellow-700 bg-primary-bg hover:bg-yellow-900 hover:text-yellow-500"
                            : getEligibilityStatus(
                                eligibilityData.listings?.eligible
                              )
                            ? "border border-primary text-primary bg-primary-bg hover:bg-primary hover:text-secondary"
                            : "border border-red-500 text-red-500 bg-primary-bg hover:bg-red-600 hover:text-white"
                        } p-1.5 px-3 rounded-md flex items-center gap-1 justify-center`}
                      >
                        {loadingEligibility ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaAmazon className="w-4 h-4" />
                        )}
                        <p className="text-xs">
                          {!authStatus?.isConnected
                            ? "Link Account"
                            : loadingEligibility
                            ? "Checking..."
                            : eligibilityData
                            ? getEligibilityStatus(
                                eligibilityData.listings?.eligible
                              )
                              ? "Eligible"
                              : "Not Eligible"
                            : "Check Eligibility"}
                        </p>
                      </button>
                      {eligibilityData &&
                        !getEligibilityStatus(
                          eligibilityData.listings?.eligible
                        ) &&
                        eligibilityData.listings?.restrictions &&
                        eligibilityData.listings.restrictions.length > 0 && (
                          <TooltipSimple
                            width={"extra"}
                            CustomJsx={() =>
                              getRestrictionsTooltipJSX(
                                eligibilityData.listings.restrictions
                              )
                            }
                          />
                        )}
                    </div>
                  </div>
                </>
              ) : (
                <div
                  role="status"
                  className="animate-pulse md:space-y-0 md:space-x-8 md:flex md:items-center flex items-center"
                >
                  <div className="w-full">
                    <div className="h-1.5 bg-secondary rounded-full max-w-[50px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[200px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[300px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[300px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[100px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[100px] mb-2.5"></div>
                    <div className="h-2 bg-secondary rounded-full max-w-[100px]"></div>
                  </div>
                  <span className="sr-only">Loading...</span>
                </div>
              )}
            </div>
            <div className="md:p-3 p-0 rounded-lg flex flex-col gap-y-3">
              <div className="md:w-32 w-24 h-24 group relative cursor-default">
                <CircularProgressbarWithChildren
                  value={loading ? 0 : scoreValue}
                  maxValue={10}
                  background={true}
                  styles={{
                    root: {},
                    path: {
                      stroke: `${
                        basicInfo?.brand === "Generic" ||
                        (parseFloat(data?.BSR?.percentage?.replace("%", "")) >
                          1.5 &&
                          salesData?.sales_estimation?.monthly_sales > 0)
                          ? "#E74B4B"
                          : scoreValue >= 7
                          ? "#76F99B"
                          : scoreValue < 7 && scoreValue >= 4
                          ? "#EAAA08"
                          : "#E74B4B"
                      }`,
                      strokeWidth: 5,
                      strokeLinecap: "butt",
                      transition: "stroke-dashoffset 0.5s ease 0s",
                      transformOrigin: "center center",
                    },
                    trail: {
                      stroke: "#1D4331",
                      strokeWidth: 4,
                      strokeLinecap: "butt",
                      transform: "rotate(0.25turn)",
                      transformOrigin: "center center",
                    },
                    background: {
                      fill: "#0A1411",
                      boxShadow:
                        "rgba(255, 255, 255, 0.3) 0px 1px 1px 0px inset, rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.5) 0px 30px 60px -30px",
                    },
                  }}
                >
                  <p className="text-white text-xs font-semibold">Scan Score</p>
                  <div className="flex">
                    <p
                      className={`font-semibold ${
                        basicInfo?.brand === "Generic" ||
                        (parseFloat(data?.BSR?.percentage?.replace("%", "")) >
                          1.5 &&
                          salesData?.sales_estimation?.monthly_sales > 0)
                          ? "text-[#E74B4B]"
                          : scoreValue >= 7
                          ? "text-primary"
                          : scoreValue < 7 && scoreValue >= 4
                          ? "text-[#EAAA08]"
                          : scoreValue === 0
                          ? "text-white"
                          : "text-[#E74B4B]"
                      } text-xl`}
                    >
                      {loading ? 0 : scoreValue}/10
                    </p>
                  </div>
                </CircularProgressbarWithChildren>
                {reasons?.length > 0 && (
                  <TooltipSimple
                    width={"extra"}
                    position={"left-[10%]"}
                    CustomJsx={() => (
                      <div className="flex flex-col gap-y-1 text-left justify-start">
                        {reasons.map((r, i) => {
                          return (
                            <div
                              key={i}
                              className={`${
                                r?.alert ? "text-yellow-500" : "text-gray-100"
                              } flex gap-x-1`}
                            >
                              {r?.alert ? (
                                <FaExclamation className="w-2 h-3 mt-0.5" />
                              ) : (
                                <GoDotFill className="w-2 h-2 mt-1" />
                              )}
                              <p className="w-[95%]">{r?.reason}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
          <div>
            <hr className="border border-secondary-bg" />
            <div className="my-4 flex gap-x-16">
              <div className="flex flex-col gap-x-1">
                <p className="text-white text-left text-xs mb-1">Alerts</p>
                <div className="flex gap-x-1">
                  {visibleAlerts && visibleAlerts.length > 0 ? (
                    visibleAlerts.map((alert, index) => (
                      <p
                        key={index}
                        className="text-white text-[10px] bg-secondary-bg rounded-md px-2 py-1"
                      >
                        {alert.title}
                      </p>
                    ))
                  ) : (
                    <p className="text-white text-[10px] bg-secondary-bg rounded-md px-2 py-1">
                      -
                    </p>
                  )}
                </div>
              </div>
              <div className="text-white text-xs">
                <p className="text-white text-left text-xs mb-1">ASIN</p>
                <div className="flex items-center gap-x-1">
                  <p className="text-xs text-gray-500">{asinValue}</p>
                  <span className="cursor-pointer text-primary bg-secondary rounded-md p-1 px-1.5">
                    <FaRegCopy
                      onClick={() => {
                        navigator.clipboard.writeText(asinValue);
                        toast.success("ASIN copied to clipboard");
                      }}
                    />
                  </span>
                </div>
              </div>
              {eanValue && (
                <div className="text-white text-xs">
                  <p className="text-white text-left text-xs mb-1">EAN</p>
                  <div className="flex items-center gap-x-1">
                    <p className="text-xs text-gray-500">{eanValue}</p>
                    <span className="cursor-pointer text-primary bg-secondary rounded-md p-1 px-1.5">
                      <FaRegCopy
                        onClick={() => {
                          navigator.clipboard.writeText(eanValue);
                          toast.success("EAN copied to clipboard");
                        }}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between gap-2.5 w-full mt-2">
              <div className="flex items-center justify-center group relative w-full bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300">
                <BsBox className="w-4.5 h-4.5" />
                <TooltipSimple
                  width={"wide"}
                  text={`<strong>Product Dimensions :</strong><br/>
                                                        Width: ${formatLength(
                                                          dimensions?.width
                                                        )} (${cmToInches(
                    dimensions?.width
                  )})<br/>
                                                        Height: ${formatLength(
                                                          dimensions?.height
                                                        )} (${cmToInches(
                    dimensions?.height
                  )})<br/>
                                                        Length: ${formatLength(
                                                          dimensions?.length
                                                        )} (${cmToInches(
                    dimensions?.length
                  )})<br/>
                                                        Weight: ${formatWeight(
                                                          dimensions?.weight
                                                        )}`}
                />
              </div>
              <div className="flex items-center justify-center group relative w-full bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300">
                <BsInfoCircle className="w-4.5 h-4.5" />
                <TooltipSimple
                  text={description || "No description"}
                  width={"extra"}
                />
              </div>
              <div
                className="flex items-center justify-center group relative w-full cursor-pointer bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300"
                onClick={() => {
                  window.open(amazonLink, "_blank");
                }}
              >
                <FaAmazon className="w-4.5 h-4.5" />
                <TooltipSimple text="View Product on Amazon" />
              </div>
              <div
                className="flex items-center justify-center group relative w-full cursor-pointer bg-secondary rounded-md px-2 py-2 text-primary hover:bg-primary hover:text-secondary transition duration-300"
                onClick={() => {
                  window.open(
                    `https://www.google.com/search?q=${encodeURIComponent(
                      basicInfo?.productName
                    )}`,
                    "_blank"
                  );
                }}
              >
                <FaGoogle className="w-4.5 h-4.5" />
                <TooltipSimple text="Search Product title on Google" />
              </div>
            </div>
            <div className="mt-4">
              <div className="">
                <div
                  className="flex cursor-pointer items-center text-white"
                  onClick={toggleAccordion}
                >
              </div>
                {/* {isOpen && (
              <ProductDetails
                loading={loading}
                data={data}
                amazonFees={amazonFees}
                salesData={salesData}
                settingsData={settingsData}
                referralFeePercent={referralFeePercent}
                alerts={alerts}
                asin={asinValue}
                basicInfo={basicInfo}
              />
            )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Overview;
